/**
 * Modal dialog for adding a property to an additionalProperties-typed
 * object map (most importantly, schema-editor `properties`/`$defs`).
 *
 * Two-stage UX:
 *  - User enters a key name (validated: non-empty, unique, no '/' or '~')
 *  - User picks a starting type — for cases where the additionalProperties
 *    schema is itself a tagged-union over types (the meta-schema's Schema
 *    def). Otherwise the type picker is hidden and the field is created
 *    directly with the additionalProperties default.
 *
 * Replaces the window.prompt + window.alert combo that the brief flagged
 * as ugly.
 */

import { resolveSchema } from '@bliksys/schema-engine';
import type { JsonSchema } from '@bliksys/shared';
import { useEffect, useId, useRef, useState } from 'react';

import './AddFieldDialog.css';

interface Props {
  /** Existing keys — the new key must not collide. */
  existingKeys: readonly string[];
  /**
   * The additionalProperties schema for the current object. If it has a
   * `oneOf` over types (the meta-schema case), we offer the type picker.
   */
  additionalPropsSchema: JsonSchema;
  /**
   * Root schema for $ref resolution. The meta-schema's `properties`
   * map has `additionalProperties: { $ref: '#/$defs/Schema' }` —
   * without resolving the ref, we'd think it has no type variants and
   * silently default every new property to the first variant (string).
   */
  rootSchema: JsonSchema;
  onConfirm: (key: string, typeChoice: string | undefined) => void;
  onCancel: () => void;
}

export function AddFieldDialog({
  existingKeys,
  additionalPropsSchema,
  rootSchema,
  onConfirm,
  onCancel,
}: Props): JSX.Element {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const nameInputRef = useRef<HTMLInputElement>(null);
  const nameId = useId();
  const typeId = useId();
  const [name, setName] = useState('');
  const [error, setError] = useState<string | null>(null);

  // If the additionalProperties schema is a oneOf over types (or a $ref to
  // a Schema def that is), surface the type variants. We compute this in
  // a defensive way — read the variants if present, else treat as a plain
  // additional-prop with no type pick.
  const typeOptions = extractTypeOptions(additionalPropsSchema, rootSchema);
  const [typeChoice, setTypeChoice] = useState<string>(typeOptions[0]?.value ?? '');

  useEffect(() => {
    const d = dialogRef.current;
    if (d && !d.open) {
      d.showModal();
      // Focus the name input on mount so keyboard users start typing
      // immediately.
      window.setTimeout(() => nameInputRef.current?.focus(), 0);
    }
  }, []);

  const handleConfirm = (): void => {
    const trimmed = name.trim();
    if (!trimmed) {
      setError('Name cannot be empty.');
      return;
    }
    if (existingKeys.includes(trimmed)) {
      setError(`A field named "${trimmed}" already exists.`);
      return;
    }
    if (trimmed.includes('/') || trimmed.includes('~')) {
      setError('Field names can\'t contain "/" or "~".');
      return;
    }
    onConfirm(trimmed, typeOptions.length > 0 ? typeChoice : undefined);
  };

  return (
    <dialog
      ref={dialogRef}
      className="add-field-dialog"
      onClose={onCancel}
      aria-labelledby="add-field-title"
    >
      <header className="add-field-header">
        <h2 id="add-field-title" className="add-field-title">
          Add field
        </h2>
      </header>

      <div className="add-field-body">
        <div className="add-field-row">
          <label htmlFor={nameId} className="add-field-label">
            Name
          </label>
          <input
            ref={nameInputRef}
            id={nameId}
            type="text"
            className="input"
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              setError(null);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleConfirm();
              }
            }}
            placeholder="e.g. title"
            autoComplete="off"
            spellCheck={false}
          />
        </div>

        {typeOptions.length > 0 && (
          <div className="add-field-row">
            <label htmlFor={typeId} className="add-field-label">
              Type
            </label>
            <select
              id={typeId}
              className="input"
              value={typeChoice}
              onChange={(e) => setTypeChoice(e.target.value)}
            >
              {typeOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
        )}

        {error && (
          <p className="add-field-error" role="alert">
            {error}
          </p>
        )}
      </div>

      <footer className="add-field-footer">
        <button type="button" className="btn-secondary" onClick={onCancel}>
          Cancel
        </button>
        <button type="button" className="btn-primary" onClick={handleConfirm}>
          Add field
        </button>
      </footer>
    </dialog>
  );
}

/**
 * Walk the additionalProperties schema looking for type variants. Handles
 * the meta-schema case where it's a $ref to a oneOf-tagged Schema def,
 * and the case where individual variants are themselves $refs (the
 * Bliksys meta-schema's Schema def: `oneOf: [{ $ref: '#/$defs/StringSchema' },
 * ...]`). Without resolving these, every "variant" looks like just
 * `{ $ref: ... }` with no `properties.type.const` to read off.
 */
function extractTypeOptions(
  schema: JsonSchema,
  rootSchema: JsonSchema,
): readonly { value: string; label: string }[] {
  const resolved = resolveSchema(schema, rootSchema);
  const variants = resolved.oneOf ?? resolved.anyOf;
  if (!variants) return [];
  const opts: { value: string; label: string }[] = [];
  for (const rawVariant of variants) {
    const variant = resolveSchema(rawVariant, rootSchema);
    const typeProp = variant.properties?.type;
    if (typeProp?.const !== undefined && typeof typeProp.const === 'string') {
      opts.push({
        value: typeProp.const,
        label: variant.title ?? capitalize(typeProp.const),
      });
    } else if (variant.title) {
      // Variants without a type pin still get listed (Reference, Union).
      opts.push({ value: variant.title, label: variant.title });
    }
  }
  return opts;
}

function capitalize(s: string): string {
  return s.length > 0 ? s.charAt(0).toUpperCase() + s.slice(1) : s;
}
